import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { ALL_ROLE_KEYS, ROLES, type RoleKey } from '../types'

interface RoleCheckboxGroupProps {
  /** Capability name; disambiguates the checkbox labels across rows. */
  label: string
  value: RoleKey[]
  onChange: (next: RoleKey[]) => void
  /** Show the roles without letting them be changed. */
  disabled?: boolean
  /**
   * Roles this capability may not be granted to, whatever the operator ticks.
   * The server refuses these saves, so offering the box would be a trap.
   */
  locked?: RoleKey[]
  /**
   * Roles held by some of what this row governs but not all, drawn as a dash.
   *
   * Only a page row has these: its boxes stand for every row on the page, and
   * a plain tick would claim agreement the rows below do not have.
   */
  mixed?: RoleKey[]
}

/** Sort into the canonical role order so stored values never depend on click order. */
function canonical(roles: RoleKey[]): RoleKey[] {
  return ALL_ROLE_KEYS.filter((key) => roles.includes(key))
}

export function RoleCheckboxGroup({
  label,
  value,
  onChange,
  disabled = false,
  locked = [],
  mixed = [],
}: RoleCheckboxGroupProps) {
  const selected = canonical(value)

  const toggle = (key: RoleKey) => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key]
    onChange(canonical(next))
  }

  return (
    <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      <legend className="sr-only">Roles for {label}</legend>
      {ROLES.map((role) => {
        const id = `${label}::${role.key}`
        const lockedByPhi = locked.includes(role.key)
        return (
          <div key={role.key} className="flex items-center gap-1.5">
            <Checkbox
              id={id}
              checked={
                selected.includes(role.key)
                  ? true
                  : mixed.includes(role.key)
                    ? 'indeterminate'
                    : false
              }
              disabled={disabled || lockedByPhi}
              onCheckedChange={() => toggle(role.key)}
            />
            <Label
              htmlFor={id}
              title={
                lockedByPhi
                  ? 'This capability handles PHI, and this role is not PHI-cleared.'
                  : undefined
              }
              className={cn(
                'text-xs font-normal whitespace-nowrap',
                disabled || lockedByPhi
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              )}>
              {role.label}
            </Label>
          </div>
        )
      })}
    </fieldset>
  )
}
